/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { createContext, useContext } from 'react';

import { AudienceType } from '../../algorithm/types';
import { useAppSelector } from '../../../../app/hooks';
import { selectIsLoadingFhirPatientData, selectPatientContext } from '../../Fhir/fhirSlice';
import { selectCreatorMode, selectDocumentVersionById, selectMessages } from '../chatSlice';
import { showPatientShowSummary } from '../../permissions';
import { contextualizationTags } from '../../Fhir/fhir_utils';

const AudienceFilterContext = createContext<AudienceType | undefined>(undefined);
export const AudienceFilterProvider = AudienceFilterContext.Provider;
const PublishedOnlyContext = createContext<boolean | undefined>(undefined);
export const PublishedOnlyProvider = PublishedOnlyContext.Provider;

export const useAudienceFilter = () => {
  const audience = useContext(AudienceFilterContext);
  if (!audience) {
    throw new Error('useAudienceFilter must be used within a AudienceFilterProvider');
  }
  return audience;
};
export const useAudienceFilterIfExists = () => useContext(AudienceFilterContext);
export const usePublishedOnly = () => {
  const publishedOnly = useContext(PublishedOnlyContext);
  if (publishedOnly === undefined) {
    throw new Error('usePublishedOnly must be used within a PublishedOnlyProvider');
  }
  return publishedOnly;
};
export const usePatientSummary = () => {
  const patientContext = useAppSelector(selectPatientContext);
  const isCreatorMode = useAppSelector(selectCreatorMode);
  const isLoadingFhirPatientData = useAppSelector(selectIsLoadingFhirPatientData);
  const isPatientSummaryEnabled = showPatientShowSummary();
  const messages = useAppSelector(selectMessages);
  const documentVersionId = messages.find((m) => m.pages?.[0])?.pages![0]!.document_version_id;
  const documentVersion = useAppSelector((state) => selectDocumentVersionById(state, documentVersionId));
  const isDocumentContextualized = contextualizationTags?.some((value) => documentVersion?.flags?.includes(value));

  const isPatientNotesSummaryEnabled =
    !isCreatorMode && isPatientSummaryEnabled && isDocumentContextualized && patientContext?.patient && messages.length;

  if (isPatientNotesSummaryEnabled && !isLoadingFhirPatientData && window.location.href.includes('/chat')) {
    //added check for url type so that it is not displayed at home page
    return true;
  } else {
    return false;
  }
};
